import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { generateEmergentPrompt } from './emergentPrompt';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Converts raw backend/AI errors into a user-friendly message.
// AI responses occasionally return malformed JSON which surfaces as a
// low-level parse error ("Expecting ',' delimiter: line ..."). Hide that.
const friendlyError = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && /expecting|delimiter|json|parse/i.test(detail)) {
    return 'The AI returned an unexpected response. Please try again.';
  }
  return detail || fallback;
};

// Encapsulates all App Studio state, AI orchestration and project history logic.
export const useAppStudio = ({ isAuthenticated, authLoading }) => {
  const messagesEndRef = useRef(null);

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [savedProjects, setSavedProjects] = useState([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);

  // Main state
  const [prompt, setPrompt] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [currentIdea, setCurrentIdea] = useState(null);
  const [currentScaffold, setCurrentScaffold] = useState(null);
  const [allGeneratedCode, setAllGeneratedCode] = useState([]);

  // Expandable sections
  const [expandedSections, setExpandedSections] = useState({});
  const [copiedItems, setCopiedItems] = useState({});

  const fetchProjects = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await axios.get(`${API}/studio/ideas`, { withCredentials: true });
      setSavedProjects(response.data || []);
    } catch (error) {
      // Non-blocking: project history simply stays empty on failure
    } finally {
      setIsLoadingProjects(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchProjects();
    }
  }, [authLoading, isAuthenticated, fetchProjects]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation]);

  const toggleSection = (id) => {
    setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedItems((prev) => ({ ...prev, [id]: true }));
    setTimeout(() => setCopiedItems((prev) => ({ ...prev, [id]: false })), 2000);
    toast.success('Copied to clipboard');
  };

  const startNew = () => {
    setConversation([]);
    setCurrentIdea(null);
    setCurrentScaffold(null);
    setAllGeneratedCode([]);
    setExpandedSections({});
  };

  const loadProject = (idea) => {
    setCurrentIdea(idea);
    setCurrentScaffold(null);
    setAllGeneratedCode([]);

    const messages = [
      { type: 'user', content: idea.description },
      {
        type: 'assistant',
        content: 'result',
        idea,
        scaffold: null,
        code: null,
        id: `result-${idea.idea_id}`,
      },
    ];

    setConversation(messages);
    toast.success(`Loaded: ${idea.name}`);
  };

  const deleteProject = async (ideaId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this project?')) return;

    try {
      await axios.delete(`${API}/studio/ideas/${ideaId}`, { withCredentials: true });
      toast.success('Project deleted');

      if (currentIdea?.idea_id === ideaId) {
        startNew();
      }

      fetchProjects();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!prompt.trim() || isProcessing) return;

    const userMessage = prompt.trim();
    setPrompt('');

    setConversation((prev) => [...prev, { type: 'user', content: userMessage }]);
    setIsProcessing(true);

    try {
      if (currentIdea) {
        // Follow-up message on an existing project
        setConversation((prev) => [...prev, {
          type: 'assistant',
          content: 'thinking',
          text: 'Processing your request...',
        }]);

        const codeResponse = await axios.post(`${API}/studio/generate-code`, {
          code_type: 'custom',
          prompt: `For the app "${currentIdea.name}" (${currentIdea.description}), generate: ${userMessage}`,
          language: 'javascript',
          framework: 'react',
        }, { withCredentials: true });

        if (userMessage.toLowerCase().includes('feature') || userMessage.toLowerCase().includes('add')) {
          const updatedIdea = await axios.put(`${API}/studio/ideas/${currentIdea.idea_id}`, {
            features: [...(currentIdea.features || []), userMessage.split(' ').slice(0, 8).join(' ')],
          }, { withCredentials: true });
          setCurrentIdea(updatedIdea.data);
          fetchProjects();
        }

        setAllGeneratedCode((prev) => [...prev, codeResponse.data]);

        setConversation((prev) => {
          const updated = prev.filter((m) => m.content !== 'thinking');
          return [...updated, {
            type: 'assistant',
            content: 'code-only',
            code: codeResponse.data,
            id: `code-${Date.now()}`,
          }];
        });
      } else {
        // New project flow
        setConversation((prev) => [...prev, {
          type: 'assistant',
          content: 'thinking',
          text: 'Analyzing your app idea...',
        }]);

        // Step 1: Create the idea
        const ideaResponse = await axios.post(`${API}/studio/ideas`, {
          name: userMessage.split(' ').slice(0, 6).join(' '),
          description: userMessage,
          status: 'idea',
          priority: 'medium',
          tags: [],
        }, { withCredentials: true });

        const idea = ideaResponse.data;
        setCurrentIdea(idea);

        // Step 2: Enhance with AI
        setConversation((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            type: 'assistant',
            content: 'thinking',
            text: 'Getting AI suggestions for features and tech stack...',
          };
          return updated;
        });

        const enhanceResponse = await axios.post(
          `${API}/studio/ideas/${idea.idea_id}/ai-enhance`,
          {},
          { withCredentials: true },
        );

        const enhancedIdea = enhanceResponse.data;
        setCurrentIdea(enhancedIdea);

        // Step 3: Generate scaffold
        setConversation((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            type: 'assistant',
            content: 'thinking',
            text: 'Generating project structure...',
          };
          return updated;
        });

        const scaffoldResponse = await axios.post(`${API}/studio/scaffold`, {
          name: enhancedIdea.name,
          description: enhancedIdea.description,
          app_type: 'fullstack',
          tech_stack: enhancedIdea.ai_suggestions?.tech_stack || {
            frontend: 'React',
            backend: 'FastAPI',
            database: 'MongoDB',
          },
          features: enhancedIdea.features || [],
        }, { withCredentials: true });

        setCurrentScaffold(scaffoldResponse.data);

        // Step 4: Generate starter code
        setConversation((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            type: 'assistant',
            content: 'thinking',
            text: 'Generating starter code...',
          };
          return updated;
        });

        const codeResponse = await axios.post(`${API}/studio/generate-code`, {
          code_type: 'react_component',
          prompt: `Main app component for: ${enhancedIdea.description}. Include the core features: ${enhancedIdea.features?.slice(0, 3).join(', ')}`,
          language: 'javascript',
          framework: 'react',
        }, { withCredentials: true });

        setAllGeneratedCode([codeResponse.data]);

        fetchProjects();

        setConversation((prev) => {
          const updated = prev.filter((m) => m.content !== 'thinking');
          return [...updated, {
            type: 'assistant',
            content: 'result',
            idea: enhancedIdea,
            scaffold: scaffoldResponse.data,
            code: codeResponse.data,
            id: `result-${Date.now()}`,
          }];
        });
      }
    } catch (error) {
      setConversation((prev) => {
        const updated = prev.filter((m) => m.content !== 'thinking');
        return [...updated, {
          type: 'assistant',
          content: 'error',
          text: friendlyError(error, 'Something went wrong. Please try again.'),
        }];
      });
      toast.error('Generation failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFollowUp = async (action, data) => {
    setIsProcessing(true);

    try {
      if (action === 'generate-more-code') {
        setConversation((prev) => [...prev, {
          type: 'assistant',
          content: 'thinking',
          text: 'Generating additional code...',
        }]);

        const response = await axios.post(`${API}/studio/generate-code`, {
          code_type: data.type,
          prompt: data.prompt,
          language: data.language || 'javascript',
          framework: data.framework || 'react',
        }, { withCredentials: true });

        setAllGeneratedCode((prev) => [...prev, response.data]);

        setConversation((prev) => {
          const updated = prev.filter((m) => m.content !== 'thinking');
          return [...updated, {
            type: 'assistant',
            content: 'code-only',
            code: response.data,
            id: `code-${Date.now()}`,
          }];
        });
      }
    } catch (error) {
      setConversation((prev) => {
        const updated = prev.filter((m) => m.content !== 'thinking');
        return [...updated, {
          type: 'assistant',
          content: 'error',
          text: friendlyError(error, 'Failed to generate code'),
        }];
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const emergentPrompt = generateEmergentPrompt(currentIdea, currentScaffold, allGeneratedCode);

  return {
    messagesEndRef,
    sidebarOpen,
    setSidebarOpen,
    savedProjects,
    isLoadingProjects,
    prompt,
    setPrompt,
    isProcessing,
    conversation,
    currentIdea,
    expandedSections,
    copiedItems,
    toggleSection,
    copyToClipboard,
    startNew,
    loadProject,
    deleteProject,
    handleSubmit,
    handleFollowUp,
    emergentPrompt,
  };
};
